import { useEffect, useState } from 'react';
import { useUser } from '@/hooks/use-user/use-user';
import { useCompaniesList } from '@/hooks/use-company/use-company';
import { useCompanySearch } from '@/hooks/use-company-search/use-company-search';
import { useCompanyAccessGuard } from '@/hooks/use-company-access-guard/use-company-access-guard';
import { useInitialSearchParams } from '@/hooks/use-initial-search-params/use-initial-search-params';
import { useUrlSync } from '@/hooks/use-url-sync/use-url-sync';
import { useUrlCompanyToStoreSync } from '@/hooks/use-url-company-sync/use-url-company-sync';
import { WithoutRange, useRangedFilter } from '@/hooks/use-time-range-selection/use-ranged-filter';
import { useCompanyStore } from '@/store/app.store';
import { DEFAULT_TIME_RANGE } from '@/store/initial-time-range';
import { readUrlString } from '@/utils/url-state/url-state';
import { DashboardFilters } from '@/services/dashboard-service/dashboard.service.types';

/**
 * The filter, URL and company spine every ranged screen sits on. Owns which company and
 * which window; the queries themselves stay with their screens.
 */
export function useScreenFilters() {
  const { companyName, setCompanyName } = useCompanyStore();

  const timeZone = Intl.DateTimeFormat().resolvedOptions().timeZone;

  const initialUrl = useInitialSearchParams();
  useUrlCompanyToStoreSync(initialUrl);

  const { userData, isFetched, isLoading: isLoadingUserData } = useUser();
  const { companies } = useCompaniesList();

  const isCompanyAdmin = userData?.roles.some((role) => role.name === 'company_admin');
  const isAdmin = userData?.roles.some((role) => role.name === 'admin');
  const defaultCompanyName = userData?.default_company || userData?.company?.name;
  const hasMultipleCompanies = (companies?.length ?? 0) > 1;

  const [pageFilter, setPageFilter] = useState<WithoutRange<DashboardFilters>>({
    company_name:
      readUrlString(initialUrl, 'company_name') ??
      companyName ??
      userData?.default_company ??
      userData?.company?.name,
  });

  const selectedFilter = useRangedFilter(pageFilter);

  useCompanyAccessGuard(selectedFilter.company_name, companies, (allowedName) => {
    setPageFilter((prev) => ({ ...prev, company_name: allowedName }));
    setCompanyName(allowedName);
  });

  useUrlSync(
    {
      time_range: selectedFilter.time_range,
      start_time: selectedFilter.start_time,
      end_time: selectedFilter.end_time,
      company_name: selectedFilter.company_name,
    },
    { defaults: { time_range: DEFAULT_TIME_RANGE } }
  );

  // Withheld until the viewer resolves, so no query goes out scoped to a company they
  // turn out not to have.
  const scopedFilter = {
    ...selectedFilter,
    time_zone: timeZone,
    company_name: isLoadingUserData ? undefined : selectedFilter.company_name,
  };

  const [companySearchQuery, setCompanySearchQuery] = useState('');
  const { companySearchData, companySearchLoading, companySearchError } = useCompanySearch({
    query: companySearchQuery,
  });

  useEffect(() => {
    if (readUrlString(initialUrl, 'company_name')) return;
    if (companyName) {
      setPageFilter((prevState) => ({ ...prevState, company_name: companyName }));
    } else if (defaultCompanyName) {
      setPageFilter((prevState) => ({
        ...prevState,
        company_name: prevState.company_name || defaultCompanyName,
      }));
    }
  }, [companyName, defaultCompanyName]);

  return {
    scopedFilter,
    selectedFilter,
    setPageFilter,
    canQuery: Boolean(isCompanyAdmin || isAdmin),
    isCompanyAdmin,
    setCompanyName,
    companies,
    hasMultipleCompanies,
    defaultCompanyName,
    isFetched,
    isLoadingUserData,
    userData,
    setCompanySearchQuery,
    companySearchData,
    companySearchLoading,
    companySearchError,
  };
}
